import { __makeTemplateObject, __rest, __assign } from '../../node_modules/tslib/tslib.es6.js';
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { Box, themeGet, Popover, Heading, Text } from '@primer/react';
import { isEnterOrSpacePressed } from '../../utils/functions.js';
import styled from 'styled-components';

var FULL_TRANSFORM = -100;
var HALF_TRANSFORM = -50;
var POPOVER_ARROW_HEIGHT = 8;
var LTPopover = function LTPopover(_a) {
  var _b = _a.ariaDescribedBy,
    ariaDescribedBy = _b === void 0 ? undefined : _b,
    _c = _a.children,
    children = _c === void 0 ? null : _c,
    _d = _a.contentId,
    contentId = _d === void 0 ? undefined : _d,
    _e = _a.contentStyles,
    contentStyles = _e === void 0 ? {} : _e,
    _f = _a.containsInteractiveChildElement,
    containsInteractiveChildElement = _f === void 0 ? false : _f,
    _g = _a.direction,
    direction = _g === void 0 ? LTPopover.Direction.TOP_RIGHT : _g,
    _h = _a.extraPopoverStyles,
    extraPopoverStyles = _h === void 0 ? {} : _h,
    _j = _a.heading,
    heading = _j === void 0 ? "" : _j,
    _k = _a.popOverbodyJSX,
    popOverbodyJSX = _k === void 0 ? null : _k,
    _l = _a.popoverConfig,
    popoverConfig = _l === void 0 ? {} : _l,
    _m = _a.openOnClick,
    openOnClick = _m === void 0 ? false : _m,
    _o = _a.tabIndex,
    tabIndex = _o === void 0 ? 0 : _o,
    _p = _a.text,
    text = _p === void 0 ? "" : _p,
    _q = _a.customOnClick,
    customOnClick = _q === void 0 ? function () {} : _q,
    _r = _a.customOnKeyDown,
    customOnKeyDown = _r === void 0 ? function () {} : _r,
    _s = _a.interactiveParent,
    interactiveParent = _s === void 0 ? false : _s,
    _t = _a.forceClose,
    forceClose = _t === void 0 ? false : _t,
    _u = _a.onOpen,
    onOpen = _u === void 0 ? function () {} : _u,
    _v = _a.usePortal,
    usePortal = _v === void 0 ? false : _v,
    _w = _a.portalTarget,
    portalTarget = _w === void 0 ? document.body : _w,
    rest = __rest(_a, ["ariaDescribedBy", "children", "contentId", "contentStyles", "containsInteractiveChildElement", "direction", "extraPopoverStyles", "heading", "popOverbodyJSX", "popoverConfig", "openOnClick", "tabIndex", "text", "customOnClick", "customOnKeyDown", "interactiveParent", "forceClose", "onOpen", "usePortal", "portalTarget"]);
  var _x = useState(false),
    isOpen = _x[0],
    setIsOpen = _x[1];
  var popoverRef = useRef(null);
  var tooltipVisibilityRef = useRef(null);
  useEffect(function () {
    if (forceClose) {
      closePopover();
    }
  }, [forceClose]);
  var contentRef = useRef(null);
  var _y = useState({
      top: "0",
      left: "0",
      transformX: "0",
      transformY: "0"
    }),
    dimension = _y[0],
    setDimension = _y[1];
  var getDimension = function getDimension() {
    var _a;
    var _dimension = __assign({}, dimension);
    var _b = (_a = contentRef === null || contentRef === void 0 ? void 0 : contentRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect(),
      left = _b.left,
      right = _b.right,
      bottom = _b.bottom,
      top = _b.top,
      width = _b.width;
    if (direction === LTPopover.Direction.TOP_RIGHT) {
      _dimension.top = bottom;
      _dimension.left = right;
      _dimension.transformX = FULL_TRANSFORM; // move popover horizontally left side of main content
    } else if (direction === LTPopover.Direction.TOP) {
      _dimension.top = bottom;
      _dimension.left = left + width / 2;
      _dimension.transformX = HALF_TRANSFORM; //move popover horizontally center of main content
    } else if (direction === LTPopover.Direction.TOP_LEFT) {
      _dimension.top = bottom;
      _dimension.left = left;
    } else if (direction === LTPopover.Direction.BOTTOM) {
      _dimension.top = top - POPOVER_ARROW_HEIGHT;
      _dimension.left = left + width / 2;
      _dimension.transformX = HALF_TRANSFORM; //move popover horizontally center of main content
      _dimension.transformY = FULL_TRANSFORM; //move popover vertically center of main content
    } else if (direction === LTPopover.Direction.BOTTOM_LEFT) {
      _dimension.top = top - POPOVER_ARROW_HEIGHT;
      _dimension.left = left;
      _dimension.transformY = FULL_TRANSFORM; //move popover vertically top of main content
    } else if (direction === LTPopover.Direction.BOTTOM_RIGHT) {
      _dimension.top = top - POPOVER_ARROW_HEIGHT;
      _dimension.left = right;
      _dimension.transformY = FULL_TRANSFORM; // move popover vertically top of main content
      _dimension.transformX = FULL_TRANSFORM; //move popover horizontally left side of main content
    }

    return _dimension;
  };
  useEffect(function () {
    if (isOpen) {
      openOnClick && document.addEventListener("click", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
      document.addEventListener("keydown", handleKeyDownForTooltipVisibility, {
        capture: true
      });
    }
    return function () {
      if (openOnClick) {
        document.removeEventListener("click", handleClickOutside);
      }
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keydown", handleKeyDownForTooltipVisibility, {
        capture: true
      });
    };
  }, [isOpen]);
  var handleClickOutside = function handleClickOutside(event) {
    if (isOpen && !(popoverRef === null || popoverRef === void 0 ? void 0 : popoverRef.current.contains(event.target))) {
      closePopover();
    }
  };
  var handleMouseEnter = function handleMouseEnter() {
    setIsOpen(true);
    onOpen();
    setDimension(getDimension());
    if (popoverRef.current) {
      popoverRef.current.focus();
    }
  };
  var handleClick = function handleClick(event) {
    if (openOnClick) {
      event.stopPropagation();
      event.preventDefault();
    }
    if (isOpen) {
      closePopover();
      return;
    }
    setDimension(getDimension());
    setIsOpen(true);
    onOpen();
    if (popoverRef.current) {
      popoverRef.current.focus();
    }
  };
  var handleKeyDown = function handleKeyDown(event) {
    if (event.key === "Escape") {
      closePopover();
    }
  };
  var handlePopoverChildClick = function handlePopoverChildClick() {
    setIsOpen(true);
    onOpen();
    if (popoverRef.current) {
      popoverRef.current.focus();
    }
  };
  function handleKeyDownForTooltipVisibility() {
    var _a;
    // 1. get the popover content div. so that we don't have to traverse whole DOM Tree
    if (contentId) {
      var contentDiv = document.getElementById("".concat(contentId, "-lt-popover-content")) || [];
      // 2. get all tooltips added in the popover content div
      var tooltips = (contentDiv === null || contentDiv === void 0 ? void 0 : contentDiv.getElementsByClassName("lt-tooltip-trigger")) || [];
      //3. Iterate over all tooltips to check if any tooltip is open.
      (_a = Array.from(tooltips)) === null || _a === void 0 ? void 0 : _a.forEach(function (tooltip) {
        // 4. if any tooltip is open it as a data attribute instant-open or delayed-open.
        var _a;
        if ((_a = tooltip === null || tooltip === void 0 ? void 0 : tooltip.getAttribute("data-state")) === null || _a === void 0 ? void 0 : _a.includes("open")) {
          // 5. tooltip is open, update the ref, so that we don't close it..
          tooltipVisibilityRef.current = true;
        }
      });
    }
  }
  function closePopover() {
    // check if tooltip is open
    if (tooltipVisibilityRef.current) {
      // timeout is added because at same time we are calling
      // closePopover from multiple places.
      // and adding delay will make sure that popover don't get closed.
      setTimeout(function () {
        tooltipVisibilityRef.current = false;
      }, 30);
      return;
    }
    // tooltip is not opened, close the popover
    setIsOpen(false);
  }
  var handleMouseLeave = function handleMouseLeave() {
    var _a;
    // If tooltip is close, only then close the popover
    if (!tooltipVisibilityRef.current) {
      if (openOnClick) {
        (_a = contentRef.current) === null || _a === void 0 ? void 0 : _a.focus();
      }
      closePopover();
    }
  };
  var popoverKeyDown = function popoverKeyDown(e) {
    if (isEnterOrSpacePressed(e)) {
      handleMouseEnter();
      customOnKeyDown(e);
      customOnClick(e);
    } else if (e.key === "Escape") {
      handleMouseLeave();
    }
  };
  var closePopoverOnBlur = function closePopoverOnBlur() {
    setTimeout(function () {
      var _a;
      //add setTimeout because document.activeElement is treated falsy by default
      // Check if the new active element is outside the popover.
      if (!((_a = popoverRef === null || popoverRef === void 0 ? void 0 : popoverRef.current) === null || _a === void 0 ? void 0 : _a.contains(document.activeElement))) {
        closePopover();
      }
    }, 0);
  };
  var closePopoverOnChildBlur = function closePopoverOnChildBlur() {
    !containsInteractiveChildElement && isOpen && closePopover();
  };
  var tooltipStyles = usePortal ? {
    top: dimension.top,
    left: dimension.left,
    transform: "translate(".concat(dimension.transformX, "%, ").concat(dimension.transformY, "%)")
  } : {
    top: "50%",
    left: "50%",
    transform: "translate(-89%, 17px)"
  };
  var disabled = rest.disabled;
  return /*#__PURE__*/React.createElement(Box, {
    position: "relative"
  }, /*#__PURE__*/React.createElement(PopOverBox, __assign({
    "aria-describedby": ariaDescribedBy,
    "aria-haspopup": "true",
    "aria-expanded": isOpen,
    justifyContent: "center",
    display: "flex",
    disabled: disabled,
    interactiveParent: interactiveParent,
    onClick: function onClick(e) {
      openOnClick && handleClick(e);
      !disabled && customOnClick(e);
    },
    onFocus: function onFocus() {
      !openOnClick && handleMouseEnter();
    },
    onMouseEnter: function onMouseEnter() {
      !openOnClick && handleMouseEnter();
    },
    onMouseLeave: function onMouseLeave() {
      !openOnClick && handleMouseLeave();
    },
    onBlur: function onBlur() {
      !openOnClick && closePopoverOnChildBlur();
    },
    onKeyDown: popoverKeyDown,
    role: "button",
    tabIndex: tabIndex,
    ref: contentRef
  }, rest), children), /*#__PURE__*/React.createElement(ConditionalPortalWrapper, {
    usePortal: usePortal,
    portalTarget: portalTarget
  }, /*#__PURE__*/React.createElement(Popover, __assign({
    absolute: true,
    caret: direction,
    id: ariaDescribedBy,
    onKeyDown: function onKeyDown(e) {
      if (e.key === "Escape") {
        handleMouseLeave();
      }
    }
  }, openOnClick ? {
    onClick: handlePopoverChildClick
  } : {
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onBlur: closePopoverOnBlur
  }, {
    open: isOpen,
    ref: popoverRef,
    role: "region",
    sx: __assign(__assign(__assign({
      position: "absolute",
      zIndex: 999
    }, tooltipStyles), {
      "&:focus-visible": {
        outline: "none"
      }
    }), extraPopoverStyles),
    tabIndex: -1
  }, popoverConfig), /*#__PURE__*/React.createElement(Popover.Content, {
    id: "".concat(contentId, "-lt-popover-content"),
    sx: __assign({
      mt: 2,
      padding: "12px"
    }, contentStyles)
  }, heading && /*#__PURE__*/React.createElement(Heading, null, heading), !!text && /*#__PURE__*/React.createElement(Text, null, text), !!popOverbodyJSX && popOverbodyJSX))));
};
function ConditionalPortalWrapper(_a) {
  var children = _a.children,
    portalTarget = _a.portalTarget,
    usePortal = _a.usePortal;
  if (usePortal) {
    return /*#__PURE__*/createPortal(children, portalTarget);
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, children);
}
LTPopover.Direction = {
  TOP: "top",
  BOTTOM: "bottom",
  LEFT: "left",
  RIGHT: "right",
  LEFT_BOTTOM: "left-bottom",
  LEFT_TOP: "left-top",
  RIGHT_BOTTOM: "right-bottom",
  RIGHT_TOP: "right-top",
  TOP_LEFT: "top-left",
  BOTTOM_LEFT: "bottom-left",
  TOP_RIGHT: "top-right",
  BOTTOM_RIGHT: "bottom-right"
};
LTPopover.propTypes = {
  ariaDescribedBy: PropTypes.string,
  children: PropTypes.node,
  contentStyles: PropTypes.object,
  direction: PropTypes.string,
  extraPopoverStyles: PropTypes.object,
  heading: PropTypes.string,
  popOverbodyJSX: PropTypes.node,
  popoverConfig: PropTypes.object,
  rest: PropTypes.object,
  tabIndex: PropTypes.number,
  text: PropTypes.string
};
var PopOverBox = styled(Box)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  cursor: ", ";\n  &:focus-visible {\n    outline: 2px solid;\n    outline-color: ", ";\n    outline-offset: -2px;\n  }\n"], ["\n  cursor: ", ";\n  &:focus-visible {\n    outline: 2px solid;\n    outline-color: ", ";\n    outline-offset: -2px;\n  }\n"])), function (props) {
  return props.disabled ? "not-allowed" : (props === null || props === void 0 ? void 0 : props.interactiveParent) ? "pointer" : "";
}, themeGet("colors.blueAccent.fg"));
var templateObject_1;

export { LTPopover as default };
